import { UnstyledButton, VisuallyHidden } from "@mantine/core";
import { Box } from "lucide-react";
import { useId, useRef } from "react";

import { Soon } from "../../../ui/Soon";
import classes from "./actions.module.css";
import type { ActionTool } from "./index";

/** Live's tag words for the two kinds of card. */
const KIND_LABEL = { function: "Function", tool: "Tool" } as const;

export interface ActionCardProps {
  action: ActionTool;
  /** The catalog's models that take the action. */
  count: number;
  /** The action the composer has now. */
  active: boolean;
  onChoose: () => void;
}

/**
 * A card of the Actions panel, as live's (capture 18; live's `action-card`): the icon and title,
 * a line or two, the "Function" or "Tool" tag and a cube with the count of models. Behind it,
 * its preview plays muted on a loop (live's own file); hovered, the preview fades in under a dark
 * top shade, from its start, the title turns white and the rest fades out (capture 19). No model
 * takes it: disabled and faded, no preview, its count gone. Its issue still open: Coming soon.
 */
export function ActionCard({ action, count, active, onChoose }: ActionCardProps) {
  const lineId = useId();
  const preview = useRef<HTMLVideoElement>(null);
  const Icon = action.icon;
  // Live rewinds the preview as the pointer comes in, so every hover shows it from its start.
  const rewind = () => {
    if (preview.current !== null) preview.current.currentTime = 0;
  };
  const card = (soon?: { "aria-disabled": true; "aria-describedby": string }) => (
    <UnstyledButton
      className={classes.card}
      data-active={active || undefined}
      disabled={count === 0}
      aria-label={action.title}
      aria-describedby={soon === undefined ? lineId : `${lineId} ${soon["aria-describedby"]}`}
      aria-disabled={soon === undefined ? undefined : true}
      onClick={soon === undefined ? onChoose : undefined}
      onMouseEnter={rewind}
    >
      <span className={classes.reveal} aria-hidden>
        <video ref={preview} src={action.preview} className={classes.preview} autoPlay muted loop playsInline />
        <span className={classes.shade} />
      </span>
      <span className={classes.cardHeader}>
        <Icon size={20} strokeWidth={1.5} className={classes.cardIcon} aria-hidden />
        <span className={classes.cardTitle}>{action.title}</span>
      </span>
      <span id={lineId} className={classes.cardBody}>
        <span className={classes.cardLine}>{action.description}</span>
        <span className={classes.cardTags}>
          <span className={classes.tag} data-kind={action.kind}>
            {KIND_LABEL[action.kind]}
          </span>
          {count === 0 ? null : (
            <span className={classes.count}>
              <Box size={16} strokeWidth={1.5} aria-hidden />
              <span aria-hidden>{count}</span>
              <VisuallyHidden>{count === 1 ? "1 model" : `${count} models`}</VisuallyHidden>
            </span>
          )}
        </span>
      </span>
    </UnstyledButton>
  );
  if (action.ready || count === 0) return card();
  return <Soon block>{(soon) => card(soon)}</Soon>;
}
